import { TextBadge } from '@/components/DesignSystem';
import { alpha, Stack } from '@mui/material';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { RewardsProgramType } from '@shared/enums/RewardsProgramType';
import { useTranslation } from 'react-i18next';

export const PROGRAM_BADGE_COLOR: Record<RewardsProgramType, string> = {
  [RewardsProgramType.Agronomy]: '#0C7C9E',
  [RewardsProgramType.Energy]: '#67790F',
};

interface ProgramTagsProps {
  isAgronomyMember?: boolean,
  isEnergyMember?: boolean,
  testID?: string,
}

export const ProgramTags = ({
  isAgronomyMember = false,
  isEnergyMember = false,
  testID = 'program-tags',
}: ProgramTagsProps) => {
  const { t } = useTranslation();
  const tags = [
    {
      isShown: isAgronomyMember,
      label: t(($) => $.programTags.agronomy),
      type: RewardsProgramType.Agronomy,
    },
    {
      isShown: isEnergyMember,
      label: t(($) => $.programTags.energy),
      type: RewardsProgramType.Energy,
    },
  ].filter(({ isShown }) => isShown);

  if (!tags.length) {
    return null;
  }

  return (
    <Stack data-testid={testID} direction="row" spacing={0.5}>
      {tags.map(({ label, type }) => (
        <Stack data-testid={`program-tag-${type}`} key={type}>
          <TextBadge
            backgroundColor={alpha(PROGRAM_BADGE_COLOR[type], 0.12)}
            text={label}
            textColor={PROGRAM_BADGE_COLOR[type]}
          />
        </Stack>
      ))}
    </Stack>
  );
};

// A retailer's chips follow the program types it runs (agronomy, energy or both).
export const RetailerProgramTags = ({
  programs,
  testID,
}: {
  programs?: Pick<ApiRewardsProgram, 'type'>[],
  testID: string,
}) => (
  <ProgramTags
    isAgronomyMember={!!programs?.some(({ type }) => type === RewardsProgramType.Agronomy)}
    isEnergyMember={!!programs?.some(({ type }) => type === RewardsProgramType.Energy)}
    testID={testID}
  />
);
